import { AttributeUiType } from 'src/app/shared/models/attributes/attributeUiType';
import { AttributeEntityType } from './attributeEntityType';

export class AttributeUiTypeDetails {
  //wired up to lock icon on the UI to add fields
  public isLocked: boolean = false;
  constructor(public uiType: AttributeUiType) {
    this.isEnumerated = !!AttributeUiTypeDetails.enumeratedUiTypes.find((x) => x === this.uiType);
  }

  get icon(): string {
    switch (this.uiType) {
      case AttributeUiType.ShortAnswer:
        return 'short_text';
      case AttributeUiType.LongAnswer:
        return 'subject';
      case AttributeUiType.Checkboxes:
        return 'check_box';
      case AttributeUiType.DropDown:
        return 'arrow_drop_down_circle';
      case AttributeUiType.Number:
        return 'looks_one';
      case AttributeUiType.Date:
        return 'event';
      case AttributeUiType.MultiSelect:
        return 'checklist';
      case AttributeUiType.Skill:
        return 'psychology';
      case AttributeUiType.StarRating:
        return 'star';
      case AttributeUiType.Document:
        return 'upload_file';
      case AttributeUiType.DateRange:
        return 'date_range';
      case AttributeUiType.Complex:
        return 'category';
      case AttributeUiType.EncryptedField:
        return 'password';
      case AttributeUiType.RadioButtonGroup:
        return 'radio_button_checked';
      case AttributeUiType.CalculatedField:
        return 'calculate';
      default:
        return 'contact_support';
    }
  }

  get name(): string {
    switch (this.uiType) {
      case AttributeUiType.ShortAnswer:
        return 'Short Answer';
      case AttributeUiType.LongAnswer:
        return 'Long Answer';
      case AttributeUiType.Checkboxes:
        return 'Checkbox';
      case AttributeUiType.DropDown:
        return 'Dropdown';
      case AttributeUiType.Number:
        return 'Number';
      case AttributeUiType.Date:
        return 'Date';
      case AttributeUiType.MultiSelect:
        return 'Multi-select';
      case AttributeUiType.Skill:
        return 'Skill';
      case AttributeUiType.StarRating:
        return 'Star Rating';
      case AttributeUiType.Document:
        return 'Document';
      case AttributeUiType.DateRange:
        return 'Date Range';
      case AttributeUiType.Complex:
        return 'Complex Field';
      case AttributeUiType.EncryptedField:
        return 'Encrypted Field';
      case AttributeUiType.RadioButtonGroup:
        return 'Radio Button Group';
      case AttributeUiType.CalculatedField:
        return 'Calculated Field';
      default:
        return 'Unknown';
    }
  }

  get hasNumberRange(): boolean {
    return this.uiType == AttributeUiType.Number;
  }

  // Does the UI currently support this AttributeUiType?
  get isSupported(): boolean {
    switch (this.uiType) {
      case AttributeUiType.ShortAnswer:
      case AttributeUiType.LongAnswer:
      case AttributeUiType.DropDown:
      case AttributeUiType.RadioButtonGroup:
      case AttributeUiType.Checkboxes:
      case AttributeUiType.MultiSelect:
      case AttributeUiType.Number:
      case AttributeUiType.StarRating:
      case AttributeUiType.Date:
      case AttributeUiType.DateRange:
        return true;
      default:
        return false;
    }
  }
  get isAdvanced(): boolean {
    switch (this.uiType) {
      case AttributeUiType.StarRating:
      case AttributeUiType.Date:
      case AttributeUiType.Document:
      case AttributeUiType.Complex:
      case AttributeUiType.EncryptedField:
        return true;
      default:
        return false;
    }
  }

  public readonly isEnumerated: boolean;

  get isSupportedForSubFieldItems(): boolean {
    switch (this.uiType) {
      case AttributeUiType.Document:
      case AttributeUiType.Complex:
      case AttributeUiType.Skill:
      case AttributeUiType.CalculatedField:
        return false;
      default:
        return true;
    }
  }

  canBeFeatured(entityType: AttributeEntityType): boolean {
    return this.isEnumerated && entityType === AttributeEntityType.Person;
  }

  canBeShownOnCreate(entityType: AttributeEntityType): boolean {
    return entityType === AttributeEntityType.Team && this.uiType !== AttributeUiType.Document;
  }

  canBeVisualized(entityType: AttributeEntityType): boolean {
    // Happens to share the same logic as canBeFeatured, but not guaranteed to always be true.
    return this.canBeFeatured(entityType);
  }

  canToggleRatingInclusion(entityType: AttributeEntityType): boolean {
    return (
      this.uiType === AttributeUiType.StarRating && entityType === AttributeEntityType.Evaluation
    );
  }

  canBeFiltered(): boolean {
    switch (this.uiType) {
      case AttributeUiType.Checkboxes:
      case AttributeUiType.DropDown:
      case AttributeUiType.ShortAnswer:
      case AttributeUiType.LongAnswer:
      case AttributeUiType.Number:
      case AttributeUiType.Date:
      case AttributeUiType.Toggle:
      case AttributeUiType.RadioButtonGroup:
      case AttributeUiType.Document:
      case AttributeUiType.MultiSelect:
      case AttributeUiType.StarRating:
      case AttributeUiType.Skill:
      case AttributeUiType.DateRange:
      case AttributeUiType.Complex:
      case AttributeUiType.EncryptedField:
      case AttributeUiType.CalculatedField:
        return true;
      default:
        return false;
    }
  }

  // Does this AttributeUiType + AttiributeEntityType combination
  // support any togglable settings?
  isToggleable(entityType: AttributeEntityType): boolean {
    return (
      this.canBeFeatured(entityType) ||
      this.canBeVisualized(entityType) ||
      this.canBeFiltered() ||
      this.canBeShownOnCreate(entityType) ||
      this.canToggleRatingInclusion(entityType)
    );
  }

  get supportsOtherValue(): boolean {
    return this.isEnumerated && this.uiType !== AttributeUiType.Skill;
  }

  static get enumeratedUiTypes(): AttributeUiType[] {
    return [
      AttributeUiType.Checkboxes,
      AttributeUiType.DropDown,
      AttributeUiType.RadioButtonGroup,
      AttributeUiType.MultiSelect,
      AttributeUiType.Skill
    ];
  }
}
